import { WINDOW_TYPES } from '../config/windowTypes';
import { formatDimension } from '../calculations/index';

export { DATE_PRESETS, getDateRangeForPreset } from './report-dates';

const TYPE_LABELS = Object.fromEntries(WINDOW_TYPES.map(t => [t.code, t.label]));

// A window only counts toward "ordered" analytics once the job it belongs to
// has actually been sold — pure estimates/quotes that never closed are excluded.
const ORDERED_STATUSES = new Set(['sold', 'in_production', 'ready', 'installed']);

export function typeLabel(code) {
  return TYPE_LABELS[code] || code || 'Unknown';
}

export function sizeLabel(width, height) {
  if (width === '' || width == null || height === '' || height == null) return 'Unknown';
  return `${formatDimension(width)}" x ${formatDimension(height)}"`;
}

// Cutlist jobs don't have a dedicated "order date" field. `soldDate` (a plain
// "YYYY-MM-DD" string) is the real signal for when a job was placed; jobs
// that predate that field, or never had it set, fall back to `createdAt`
// (a Firestore Timestamp) so they still land in date-range reports.
function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  return null;
}

function getJobDate(job) {
  return toDate(job.soldDate) || toDate(job.createdAt);
}

// Rounds to a fixed precision before it ever becomes part of a grouping key,
// so trailing floating-point noise (e.g. 20.499999999999996) can never split
// what is really the same size into two rows.
function roundInches(n) {
  return Math.round(n * 10000) / 10000;
}

// Flattens every window line-item across sold-or-further cutlist jobs into
// one row per window, optionally restricted to an order-date range.
export function flattenOrderedWindows(cutlistJobs, { from, to } = {}) {
  const rows = [];

  cutlistJobs.forEach(job => {
    if (!ORDERED_STATUSES.has(job.status)) return;

    const orderDate = getJobDate(job);
    if (from && (!orderDate || orderDate < from)) return;
    if (to && orderDate && orderDate > to) return;

    (job.windows || []).forEach(w => {
      if (!w.width || !w.height) return;
      const width = roundInches(Number(w.width));
      const height = roundInches(Number(w.height));
      if (!Number.isFinite(width) || !Number.isFinite(height)) return;
      const qty = Number(w.qty) > 0 ? Number(w.qty) : 1;

      rows.push({
        jobId: job.id,
        customerName: job.customerName || 'Unknown',
        orderDate: orderDate ? orderDate.toISOString().slice(0, 10) : null,
        type: w.type,
        typeLabel: typeLabel(w.type),
        width,
        height,
        size: sizeLabel(width, height),
        config: w.config || '',
        color: w.color || '',
        glassType: w.glassType || '',
        glassConfig: w.glassConfig || '',
        qty,
        sqFt: (width * height / 144) * qty,
      });
    });
  });

  return rows;
}

// Shared grouping engine: buckets rows by `key(row)`, counts line items and
// total qty per bucket, tracks distinct jobs, and ranks by total qty.
// `initExtra`/`updateExtra` let each report attach its own descriptive
// fields (labels, per-bucket sets, etc.) without duplicating the counting
// logic five times over.
function aggregateRows(rows, { key, initExtra, updateExtra }) {
  const map = new Map();

  rows.forEach(r => {
    const k = key(r);
    if (!map.has(k)) {
      map.set(k, {
        key: k,
        timesOrdered: 0,
        totalQty: 0,
        sqFt: 0,
        jobIds: new Set(),
        ...(initExtra ? initExtra(r) : {}),
      });
    }
    const entry = map.get(k);
    entry.timesOrdered += 1;
    entry.totalQty += r.qty;
    entry.sqFt += r.sqFt || 0;
    entry.jobIds.add(r.jobId);
    if (updateExtra) updateExtra(entry, r);
  });

  const totalQtyAll = rows.reduce((sum, r) => sum + r.qty, 0);

  return Array.from(map.values())
    .map(e => ({
      ...e,
      jobCount: e.jobIds.size,
      pctOfTotal: totalQtyAll > 0 ? (e.totalQty / totalQtyAll) * 100 : 0,
    }))
    .sort((a, b) => b.totalQty - a.totalQty);
}

function bumpTally(map, key, amount) {
  map.set(key, (map.get(key) || 0) + amount);
}

// Turns a value->qty tally into a ranked list and a "Value ×N, Value ×N" label.
// Every distinct value (e.g. "White" vs "Dk Woodgrain/White") stays its own
// entry with its own count — never merged or fuzzy-matched into another.
function finalizeTally(map) {
  const list = Array.from(map.entries())
    .map(([value, qty]) => ({ value, qty }))
    .sort((a, b) => b.qty - a.qty);
  const label = list.map(e => (list.length > 1 ? `${e.value} ×${e.qty}` : e.value)).join(', ');
  return { list, label };
}

// Ranks distinct (type, EXACT width x height, COLOR) combinations by units
// ordered. Color is part of the grouping key, not just a tally — a White
// 20x40 Double Hung and a Dk Woodgrain/White 20x40 Double Hung are different
// products (different vinyl stock), so they are always separate rows, never
// blended into one "sizes" line. Config is a secondary detail (layout, not
// a different physical part) and stays a per-row tally.
export function summarizeBySize(rows) {
  return aggregateRows(rows, {
    key: r => `${r.type}|${r.width}|${r.height}|${r.color || 'Unspecified'}`,
    initExtra: r => ({
      type: r.type, typeLabel: r.typeLabel, size: r.size, width: r.width, height: r.height,
      color: r.color || 'Unspecified', configTally: new Map(),
    }),
    updateExtra: (entry, r) => {
      bumpTally(entry.configTally, r.config || 'Unspecified', r.qty);
    },
  }).map(e => {
    const configs = finalizeTally(e.configTally);
    return { ...e, configs: configs.label, configList: configs.list };
  });
}

export function summarizeByType(rows) {
  return aggregateRows(rows, {
    key: r => r.type,
    initExtra: r => ({ type: r.type, typeLabel: r.typeLabel }),
  });
}

export function summarizeByConfig(rows) {
  return aggregateRows(rows, {
    key: r => `${r.type}|${r.config || 'Unspecified'}`,
    initExtra: r => ({ type: r.type, typeLabel: r.typeLabel, config: r.config || 'Unspecified' }),
  });
}

export function summarizeByGlass(rows) {
  return aggregateRows(rows, {
    key: r => `${r.glassType || 'Unspecified'}|${r.glassConfig || 'Unspecified'}`,
    initExtra: r => ({ glassType: r.glassType || 'Unspecified', glassConfig: r.glassConfig || 'Unspecified' }),
  });
}

export function summarizeByColor(rows) {
  return aggregateRows(rows, {
    key: r => r.color || 'Unspecified',
    initExtra: r => ({ color: r.color || 'Unspecified' }),
  });
}

export function summarizeByCustomer(rows) {
  return aggregateRows(rows, {
    key: r => r.customerName || 'Unknown',
    initExtra: r => ({ customerName: r.customerName || 'Unknown' }),
  });
}

// Quantity ordered per calendar month, oldest first — feeds a trend view.
export function summarizeByMonth(rows) {
  const map = new Map();

  rows.forEach(r => {
    if (!r.orderDate) return;
    const d = new Date(r.orderDate);
    if (Number.isNaN(d.getTime())) return;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (!map.has(key)) {
      map.set(key, { key, label: d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }), totalQty: 0, timesOrdered: 0 });
    }
    const entry = map.get(key);
    entry.totalQty += r.qty;
    entry.timesOrdered += 1;
  });

  return Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));
}
